import { createFeature, createReducer, on } from '@ngrx/store';

import { defaultMultiUserPreferences, MultiUserPreferences } from 'app/features/settings/models';
import { prependUniqueNumber } from 'app/shared/utils';
import {
    initializedMultiUserPreferenceStore,
    resetMultiUserPreferences,
    updatingMultiUserRecentActivities,
    updatingMultiUserRecentEmployeeNumber,
    updatingMultiUserRecentProductBoxNumber,
    updatingSortActivityModeInMultiUserPreferences,
} from 'app/state/multi-user-preferences/multi-user-preferences.actions';

const maxLengthRecentActivitiesInMultiUserPreferences = 24;
export const multiUserPreferencesFeatureKey = 'preferences';

export interface MultiUserPreferencesState {
    multiUserPreferences: MultiUserPreferences | undefined;
}

export const initialState: MultiUserPreferencesState = {
    multiUserPreferences: undefined,
};

export const reducer = createReducer(
    initialState,
    on(initializedMultiUserPreferenceStore, (state, { preferences }) => ({
        ...state,
        multiUserPreferences: preferences,
    })),
    on(resetMultiUserPreferences, (state) => ({
        ...state,
        multiUserPreferences: defaultMultiUserPreferences,
    })),
    on(updatingMultiUserRecentActivities, (state, { activityNumber }) => {
        const recentActivityNumbers = prependUniqueNumber({
            numberToPrepend: activityNumber,
            currentArray: state.multiUserPreferences?.recentActivityNumbers ?? [],
            maxLength: maxLengthRecentActivitiesInMultiUserPreferences,
        });

        return {
            ...state,
            multiUserPreferences: {
                ...defaultMultiUserPreferences,
                ...state.multiUserPreferences,
                recentActivityNumbers,
            },
        };
    }),
    on(
        updatingSortActivityModeInMultiUserPreferences,
        (state, { sortActivityMode }): MultiUserPreferencesState => {
            if (state.multiUserPreferences?.sortActivityMode === sortActivityMode) return state;
            return {
                ...state,
                multiUserPreferences: {
                    ...defaultMultiUserPreferences,
                    ...state.multiUserPreferences,
                    sortActivityMode,
                },
            };
        },
    ),
    on(updatingMultiUserRecentEmployeeNumber, (state, action) => {
        const recentEmployeeNumbers = prependUniqueNumber({
            numberToPrepend: action.employeeNumber,
            currentArray: state.multiUserPreferences?.recentEmployeeNumbers ?? [],
            maxLength: 20,
        });
        return {
            ...state,
            multiUserPreferences: {
                ...defaultMultiUserPreferences,
                ...state.multiUserPreferences,
                recentEmployeeNumbers,
            },
        };
    }),
    on(updatingMultiUserRecentProductBoxNumber, (state, { recentProductBoxNumber }) => {
        if (state.multiUserPreferences?.recentProductBoxNumber === recentProductBoxNumber) return state;
        return {
            ...state,
            multiUserPreferences: {
                ...defaultMultiUserPreferences,
                ...state.multiUserPreferences,
                recentProductBoxNumber,
            },
        };
    }),
);

export const multiUserPreferencesFeature = createFeature({
    name: multiUserPreferencesFeatureKey,
    reducer,
});
